import * as React from 'react';
import SortButton from './sort-button';
import { makeStyles, Theme } from '@material-ui/core';
import { neutral } from '../theme/colors';
import TextStyles from '../theme/text';
import clsx from 'clsx';

interface Props {
  /** text displayed in the th cell */
  title: string;
  /** th attribute defining the values the header pertains to */
  scope?: 'col' | 'row';
  /** name of the field ordered by this column */
  orderField?: string;
  /** orderField currently being used for sort */
  orderColumn?: string;
  /** current sort direction */
  orderDirection?: 'asc' | 'desc' | null;
  /** handler to be called on click events to reorder columns */
  reorderHandler?: (orderField: string, direction: 'asc' | 'desc') => void;
  /** colspan to forward to inner th element */
  colspan?: number;
  /** className for extra styles */
  className?: string;
}

const useStyles = makeStyles((theme: Theme) => ({
  cell: {
    ...TextStyles.label1,
    zIndex: 100,
    textTransform: 'uppercase',
    position: 'sticky',
    top: 0,
    background: neutral[100],
    whiteSpace: 'nowrap',
    paddingTop: 0,
    paddingRight: 20,
    paddingBottom: 11,
    '&:first-child': {
      paddingLeft: 38
    },
    '&::after': {
      content: "''",
      position: 'absolute',
      width: '100%',
      bottom: '1px',
      left: 0,
      height: '1px',
      backgroundColor: neutral[250]
    }
  },
  current: {
    '& $inner > button': {
      fontWeight: 600
    }
  },
  inner: {
    display: 'flex',
    justifyContent: 'flex-start',
    alignItems: 'center',
    height: 22,
    '&> button': {
      ...TextStyles.label1,
      textTransform: 'uppercase',
      background: neutral[100],
      whiteSpace: 'nowrap',
      border: 'none',
      padding: 0,
      cursor: 'pointer'
    }
  },
  title: {},
  sortGroup: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    marginLeft: 4
  }
}));

/**
 * HeaderCell -- renders th in table header with optional sorting buttons
 */
const HeaderCell = ({
  title,
  scope,
  orderField,
  orderColumn,
  orderDirection,
  reorderHandler,
  colspan,
  className
}: Props) => {
  const classes = useStyles();
  const clickHandler = () => {
    let direction: 'asc' | 'desc';
    if (orderField !== orderColumn) {
      direction = 'asc';
    } else {
      direction = orderDirection === 'asc' ? 'desc' : 'asc';
    }
    if (reorderHandler && orderField) {
      reorderHandler(orderField, direction);
    }
  };
  return (
    <th
      className={clsx(classes.cell, orderField === orderColumn && classes.current, className)}
      scope={scope}
      colSpan={colspan}
    >
      <div className={classes.inner}>
        {orderColumn && reorderHandler && orderField ? (
          <React.Fragment>
            <button
              className={classes.title}
              aria-label={`Order by ${title}`}
              title={`Order by ${title}`}
              onClick={clickHandler}
            >
              {title || ''}
            </button>
            <div className={classes.sortGroup}>
              <SortButton
                direction="Ascending"
                current={orderField === orderColumn && orderDirection === 'asc'}
                onClick={() => reorderHandler(orderField, 'asc')}
              />
              <SortButton
                direction="Descending"
                current={orderField === orderColumn && orderDirection === 'desc'}
                onClick={() => reorderHandler(orderField, 'desc')}
              />
            </div>
          </React.Fragment>
        ) : (
          <span className={classes.title}>{title || ''}</span>
        )}
      </div>
    </th>
  );
};

export default HeaderCell;
